/**
 * HTML → Markdown / plain text, for `document_read`'s `format` argument.
 *
 * Documents are stored as `bodyHtml` (always) plus `bodyLexical` (sometimes).
 * `bodyLexical` NEVER leaves the app: it is the editor's internal snapshot
 * format, and handing it out invites a client to write a malformed node tree
 * back into a document that has no undo across sync.
 *
 * This is deliberately a small block-level converter rather than a dependency:
 * headings, paragraphs, lists, blockquotes, code, links, emphasis, rules,
 * and checklists. **What it does not do** is round-trip: tables, images,
 * footnotes and anything the editor renders with its own markup come out as
 * their text content. `document_write` takes markdown through the app's own
 * `markdownToHtml`, so the write direction is the app's existing converter and
 * not this one.
 *
 * Images and videos come out as a readable "🖼 Image: name" line in markdown
 * and text, and the html format drops their decryption keys (card MCP 1 — see
 * `mediaKeys.js`). Every format leaves through `renderBody`, so that holds for
 * every reader.
 */
import { mediaToLabels, stripMediaKeyAttrs } from './mediaKeys';

const BLOCK = new Set([
  'p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
  'blockquote', 'pre', 'hr', 'table', 'tr', 'section', 'article',
]);

function decodeEntities(s) {
  return String(s)
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/** Plain text: tags stripped, block boundaries become newlines. */
export function htmlToText(html) {
  if (!html) return '';
  const withBreaks = String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(new RegExp(`</(${[...BLOCK].join('|')})>`, 'gi'), '\n');
  return decodeEntities(withBreaks.replace(/<[^>]*>/g, ''))
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Lexical saves bold as <b><strong>…</strong></b> and italic as <i><em>…</em></i>
 * — ONE format each. Collapsed first, or italic came out as "**it**" (bold)
 * and bold as "****bold****" (02.10.2026).
 */
function collapseSameFormat(html) {
  return String(html)
    .replace(/<(?:b|strong)\b[^>]*>(\s*)<(?:b|strong)\b[^>]*>/gi, '<strong>$1')
    .replace(/<\/(?:b|strong)>(\s*)<\/(?:b|strong)>/gi, '$1</strong>')
    .replace(/<(?:i|em)\b[^>]*>(\s*)<(?:i|em)\b[^>]*>/gi, '<em>$1')
    .replace(/<\/(?:i|em)>(\s*)<\/(?:i|em)>/gi, '$1</em>');
}

function inline(html) {
  return decodeEntities(
    collapseSameFormat(html)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/gi, (_m, _t, inner) => `**${inline(inner)}**`)
      .replace(/<(em|i)\b[^>]*>([\s\S]*?)<\/\1>/gi, (_m, _t, inner) => `*${inline(inner)}*`)
      .replace(/<code\b[^>]*>([\s\S]*?)<\/code>/gi, (_m, inner) => `\`${htmlToText(inner)}\``)
      .replace(/<a\b[^>]*href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi,
        (_m, href, inner) => `[${inline(inner)}](${href})`)
      .replace(/<[^>]*>/g, '')
  ).trim();
}

/**
 * Markdown, block by block. The regex walk is intentional: `DOMParser` is
 * available in the webview but this module is also read by tests that run
 * without one, and a document body is our own sanitised HTML rather than
 * arbitrary web markup.
 */
export function htmlToMarkdown(html) {
  if (!html) return '';
  let out = String(html);

  out = out.replace(/<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi,
    (_m, level, inner) => `\n${'#'.repeat(Number(level))} ${inline(inner)}\n`);

  out = out.replace(/<pre\b[^>]*>([\s\S]*?)<\/pre>/gi,
    (_m, inner) => `\n\`\`\`\n${htmlToText(inner)}\n\`\`\`\n`);

  out = out.replace(/<blockquote\b[^>]*>([\s\S]*?)<\/blockquote>/gi, (_m, inner) =>
    `\n${htmlToText(inner).split('\n').map((l) => `> ${l}`).join('\n')}\n`);

  out = out.replace(/<ul\b[^>]*>([\s\S]*?)<\/ul>/gi, (_m, inner) =>
    `\n${listItems(inner).map((item) => `- ${item}`).join('\n')}\n`);

  out = out.replace(/<ol\b[^>]*>([\s\S]*?)<\/ol>/gi, (_m, inner) =>
    `\n${listItems(inner).map((item, i) => `${i + 1}. ${item}`).join('\n')}\n`);

  out = out.replace(/<hr\s*\/?>/gi, '\n---\n');
  out = out.replace(/<p\b[^>]*>([\s\S]*?)<\/p>/gi, (_m, inner) => `\n${inline(inner)}\n`);
  out = out.replace(/<div\b[^>]*>([\s\S]*?)<\/div>/gi, (_m, inner) => `\n${inline(inner)}\n`);

  // A top-level <br> (an image label between blocks) is a line break, not nothing.
  return decodeEntities(out.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, ''))
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function listItems(inner) {
  const items = [];
  const re = /<li\b([^>]*)>([\s\S]*?)<\/li>/gi;
  let match = re.exec(inner);
  while (match) {
    const attrs = match[1] || '';
    const text = inline(match[2]);
    // Lexical renders a checklist item with an aria-checked attribute; keeping
    // the box is worth two lines because an agent reading a todo list should
    // see which items are done.
    if (/role=["']checkbox["']/i.test(attrs) || /aria-checked/i.test(attrs)) {
      items.push(`${/aria-checked=["']true["']/i.test(attrs) ? '[x]' : '[ ]'} ${text}`);
    } else {
      items.push(text);
    }
    match = re.exec(inner);
  }
  return items;
}

/** The one place `format` is turned into text, so every reader agrees. */
export function renderBody(bodyHtml, format) {
  if (format === 'html') return stripMediaKeyAttrs(bodyHtml || '');
  const readable = mediaToLabels(bodyHtml || '');
  if (format === 'text') return htmlToText(readable);
  return htmlToMarkdown(readable);
}
